import type { FieldError } from './fieldSpecs';

/**
 * Load documents (design 5.6): the kinds, the storage path `load_document_add` checks, a file name the
 * Storage API accepts as a key, and the bucket's limits (`load-docs`: 25 MiB; pdf, jpeg, png, heic, webp).
 */

/** The load kinds of `document_kind`, in display order. */
export const DOCUMENT_KINDS = [
  'rate_confirmation',
  'bol',
  'pod',
  'lumper_receipt',
  'fuel_receipt',
  'detention',
  'tonu',
  'other',
] as const;
export type DocumentKind = (typeof DOCUMENT_KINDS)[number];

/** The office may add any load kind (pod / bol also arrive from the driver's app). */
export const OFFICE_KINDS: readonly DocumentKind[] = DOCUMENT_KINDS;

export function isDocumentKind(v: unknown): v is DocumentKind {
  return typeof v === 'string' && (DOCUMENT_KINDS as readonly string[]).includes(v);
}

/** `rate_confirmation` -> `documentKind.rateConfirmation`. */
export function documentKindKey(kind: string): string {
  return `documentKind.${kind.replace(/_([a-z])/g, (_, c: string) => c.toUpperCase())}`;
}

export const MAX_FILE_BYTES = 25 * 1024 * 1024;
export const ALLOWED_TYPES = ['application/pdf', 'image/jpeg', 'image/png', 'image/heic', 'image/webp'] as const;
const TYPE_BY_EXT: Record<string, string> = {
  pdf: 'application/pdf',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  heic: 'image/heic',
  webp: 'image/webp',
};
/** The file input's `accept`: types and extensions (an iPhone HEIC often has no type). */
export const ACCEPT = [...ALLOWED_TYPES, ...Object.keys(TYPE_BY_EXT).map((e) => `.${e}`)].join(',');

const MAX_NAME = 100;

/**
 * A storage key segment from a file name: the last path part only, accents folded, anything outside
 * `[A-Za-z0-9._-]` (separators, control characters, `#?%`, spaces, emoji) replaced by `-`, runs
 * collapsed, at most 100 characters with the extension kept; nothing left -> `document`.
 */
export function safeFileName(name: string): string {
  const last = name.split(/[/\\]/).pop() ?? '';
  let s = last
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^A-Za-z0-9._-]/g, '-')
    .replace(/-+/g, '-')
    .replace(/^[-.]+/, '')
    .replace(/-+$/, '');
  if (!s || s === '.') return 'document';
  if (s.length > MAX_NAME) {
    const dot = s.lastIndexOf('.');
    const ext = dot > 0 && s.length - dot <= 10 ? s.slice(dot) : '';
    s = s.slice(0, MAX_NAME - ext.length) + ext;
  }
  return s;
}

/** `{agency}/{load}/{kind}/{uuid}-{name}`: exactly 4 segments, the shape `load_document_add` checks. */
export function storagePath(agencyId: string, loadId: string, kind: DocumentKind, uuid: string, name: string): string {
  return `${agencyId}/${loadId}/${kind}/${uuid}-${safeFileName(name)}`;
}

/** What a register needs of a file (a `File`, or the name / type / size a create result kept). */
export type FileLike = { name: string; type: string; size: number };

const extOf = (name: string) => (name.includes('.') ? name.split('.').pop()!.toLowerCase() : '');

/** The type to send: the browser's, or the one of the extension when the browser gives none (HEIC). */
export function contentTypeOf(file: FileLike): string {
  return file.type || TYPE_BY_EXT[extOf(file.name)] || '';
}

/** null, or the `validation.*` key the picker shows. */
export function checkFile(file: FileLike): FieldError | null {
  const type = contentTypeOf(file);
  if (!(ALLOWED_TYPES as readonly string[]).includes(type)) return { key: 'validation.fileType' };
  if (file.size <= 0) return { key: 'validation.fileEmpty' };
  if (file.size > MAX_FILE_BYTES) return { key: 'validation.fileTooLarge' };
  return null;
}
